import React, { useState, useEffect } from 'react';
import { validateImageUrl } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface ProductImageProps {
  imageUrl: string;
  title: string;
  isLoading?: boolean; // Prop is named isLoading
  isNewUpload?: boolean;
}

const ProductImage = ({
  imageUrl,
  title,
  isLoading: parentIsLoadingProp = false, // Changed: Use 'isLoading' from props and alias it
  isNewUpload = false
}: ProductImageProps) => {
  const [imageError, setImageError] = useState(false);
  const [processedImageUrl, setProcessedImageUrl] = useState<string>('');
  const [retryCount, setRetryCount] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  const placeholderImage = '/placeholder.svg';
  const fallbackPlaceholder = 'https://placeholder.pics/svg/300x240/DEDEDE/555555?text=No+Image';

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    setRetryCount(0);
    
    if (!imageUrl || imageUrl.trim() === '') {
      setProcessedImageUrl(placeholderImage);
      setImageLoaded(true); // Placeholder is considered "loaded"
      return;
    }
    
    const cleanedUrl = validateImageUrl(imageUrl);
    if (!cleanedUrl || cleanedUrl.trim() === '') {
      setProcessedImageUrl(placeholderImage);
      setImageLoaded(true); // Placeholder for invalid cleaned URL
      return;
    }
    
    setProcessedImageUrl(cleanedUrl);
  }, [imageUrl]);

  const handleImageError = () => {
    if (processedImageUrl && processedImageUrl !== placeholderImage && processedImageUrl !== fallbackPlaceholder) {
      // console.warn(`Image load failed for: ${processedImageUrl}. Original: ${imageUrl}`);
    }

    if (processedImageUrl === fallbackPlaceholder) {
      if (!imageError) setImageError(true);
      if (!imageLoaded) setImageLoaded(true);
      return;
    }

    if (processedImageUrl === placeholderImage) {
      setProcessedImageUrl(fallbackPlaceholder);
      setImageLoaded(false);
      setImageError(false);
      return;
    }
    
    if (retryCount < 1) {
      setRetryCount(count => count + 1);
      const baseUrl = imageUrl.split('?')[0];
      setProcessedImageUrl(baseUrl);
      setImageLoaded(false);
      setImageError(false);
    } else {
      setProcessedImageUrl(fallbackPlaceholder);
      setImageLoaded(false);
      setImageError(true);
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const imageToDisplay = imageError && processedImageUrl !== fallbackPlaceholder
    ? fallbackPlaceholder
    : processedImageUrl || placeholderImage;

  // Use the aliased prop 'parentIsLoadingProp' here
  const isLoading = parentIsLoadingProp || (!imageLoaded && !imageError && imageToDisplay !== placeholderImage && imageToDisplay !== fallbackPlaceholder);

  return (
    <div className="relative w-full h-48 overflow-hidden bg-gray-100 rounded-t-xl">
      {isLoading && (
        <Skeleton className="w-full h-full absolute inset-0 rounded-t-xl" />
      )}

      <img
        src={imageToDisplay}
        alt={title || 'Product image'}
        className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        loading="lazy"
        onError={handleImageError}
        onLoad={handleImageLoad}
        key={imageToDisplay}
      />
    </div>
  );
};

export default ProductImage;
